import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Copy, Trash2, Archive, CheckCircle2, X, Search } from 'lucide-react';

// Liste complète des vitamines disponibles
const VITAMINES_DISPONIBLES = [
  // === PRODUITS MULTI-VITAMINES ===
  'Cernevit (Multivitamines IV - A, B1, B2, B3, B5, B6, B7, B9, B12, C, D3, E)',
  // === VITAMINES ===
  'Vitamine A (Rétinol)', 'Bêta-Carotène',
  'Vitamine C (Acide ascorbique)', 'Vitamine C haute dose (10-25g)', 'Vitamine C mega dose (>25g)',
  'Vitamine B1 (Thiamine)', 'Vitamine B2 (Riboflavine)', 'Vitamine B3 (Niacine/Niacinamide)',
  'Vitamine B5 (Acide pantothénique)', 'Vitamine B6 (Pyridoxine)', 'Vitamine B7 (Biotine)',
  'Vitamine B9 (Acide folique)', 'Vitamine B12 (Cobalamine/Cyanocobalamine)',
  'Vitamine B12 (Méthylcobalamine)', 'Vitamine B12 (Hydroxocobalamine)', 'Vitamines B Complex (B1-B12)',
  'Vitamine D2 (Ergocalciférol)', 'Vitamine D3 (Cholécalciférol)',
  'Vitamine E (Tocophérol)', 'Vitamine E mixte (Tocophérols)',
  'Vitamine K1 (Phylloquinone)', 'Vitamine K2 (Ménaquinone)',
  // === MINÉRAUX ===
  'Calcium (Gluconate)', 'Calcium (Chlorure)', 'Magnésium (Sulfate)', 'Magnésium (Chlorure)',
  'Potassium (Chlorure)', 'Sodium (Chlorure)', 'Phosphore', 'Zinc (Sulfate)', 'Zinc (Gluconate)',
  'Fer (Gluconate)', 'Fer (Sucrose)', 'Cuivre (Gluconate)', 'Manganèse (Sulfate)',
  'Chrome (Chlorure)', 'Sélénium (Sélénite)', 'Molybdène', 'Iode (Iodure)',
  // === ANTIOXYDANTS ===
  'Glutathion réduit (GSH)', 'Glutathion liposomal', 'N-Acétylcystéine (NAC)',
  'Acide Alpha-Lipoïque (ALA)', 'Coenzyme Q10 (CoQ10)', 'Ubiquinol (forme réduite CoQ10)',
  'Acide R-Lipoïque', 'Superoxide Dismutase (SOD)', 'Catalase', 'Resvératrol',
  'Quercétine', 'Curcumine', 'Astaxanthine', 'Lutéine', 'Zéaxanthine', 'Lycopène', 'Pycnogénol',
  // === ACIDES AMINÉS ===
  'L-Arginine', 'L-Carnitine', 'L-Carnosine', 'L-Cystéine', 'L-Glutamine', 'L-Glutathion',
  'L-Glycine', 'L-Histidine', 'L-Isoleucine', 'L-Leucine', 'L-Lysine', 'L-Méthionine',
  'L-Phénylalanine', 'L-Proline', 'L-Sérine', 'L-Taurine', 'L-Thréonine', 'L-Tryptophane',
  'L-Tyrosine', 'L-Valine', 'Acides aminés branchés (BCAA)', 'Acides aminés essentiels (EAA)',
  // === PEPTIDES & PROTÉINES ===
  'Collagène hydrolysé', 'Collagène type I', 'Collagène type II', 'Collagène type III',
  'Peptides de collagène', 'Glutathion peptide', 'Thymosin alpha-1', 'BPC-157',
  // === AUTRES ===
  'EDTA', 'DMPS', 'DMSA', 'NAD+', 'NADH', 'PQQ', 'Phosphatidylcholine', 'Phosphatidylsérine',
  'Oméga-3 (EPA/DHA)', 'Choline', 'Inositol', 'MSM', 'Acide Hyaluronique', 'D-Ribose',
  'Créatine', 'SAMe', 'Alpha-GPC', 'Acétyl-L-Carnitine', 'Autre (à préciser)'
].sort();

const ProtocolsManager = () => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [searchVitamine, setSearchVitamine] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    vitamines: [],
    dosage: 'Standard',
    debit: '125',
    duree: 45,
    description: '',
    statut: 'actif'
  });

  useEffect(() => {
    fetchProtocols();
  }, []);

  const fetchProtocols = async () => {
    try {
      const response = await axios.get(`${API}/protocols`);
      setProtocols(response.data);
    } catch (error) {
      console.error('Error fetching protocols:', error);
      toast.error('Erreur lors du chargement des protocoles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProtocol(null);
    setFormData({
      nom: '',
      vitamines: [],
      dosage: 'Standard',
      debit: '125',
      duree: 45,
      description: '',
      statut: 'actif'
    });
    setSearchVitamine('');
    setShowDialog(true);
  };

  const handleEdit = (protocol) => {
    setEditingProtocol(protocol);
    setFormData({
      ...protocol,
      vitamines: protocol.vitamines || []
    });
    setSearchVitamine('');
    setShowDialog(true);
  };

  const handleDuplicate = (protocol) => {
    setEditingProtocol(null);
    setFormData({
      ...protocol,
      nom: `${protocol.nom} (Copie)`,
      id: undefined,
      vitamines: protocol.vitamines || []
    });
    setSearchVitamine('');
    setShowDialog(true);
  };

  const addVitamine = (vitamine) => {
    if (vitamine && !formData.vitamines.includes(vitamine)) {
      setFormData({
        ...formData,
        vitamines: [...formData.vitamines, vitamine]
      });
      setSearchVitamine('');
    }
  };

  const removeVitamine = (vitamine) => {
    setFormData({
      ...formData,
      vitamines: formData.vitamines.filter(v => v !== vitamine)
    });
  };

  const handleSave = async () => {
    if (!formData.nom.trim()) {
      toast.error('Le nom du protocole est requis');
      return;
    }
    if (formData.vitamines.length === 0) {
      toast.error('Ajoutez au moins une vitamine au protocole');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        debit: formData.debit.toString().includes('mL/h') ? formData.debit : `${formData.debit} mL/h`
      };

      if (editingProtocol) {
        await axios.put(`${API}/protocols/${editingProtocol.id}`, dataToSend);
        toast.success('✅ Protocole modifié avec succès');
      } else {
        await axios.post(`${API}/protocols`, dataToSend);
        toast.success('✅ Protocole créé avec succès');
      }
      await fetchProtocols();
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving protocol:', error);
      toast.error('❌ Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (protocolId) => {
    if (!window.confirm('⚠️ Supprimer définitivement ce protocole ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/protocols/${protocolId}`);
      toast.success('✅ Protocole supprimé');
      await fetchProtocols();
    } catch (error) {
      console.error('Error deleting protocol:', error);
      toast.error('❌ Erreur lors de la suppression');
    }
  };

  const toggleStatus = async (protocol) => {
    const newStatus = protocol.statut === 'actif' ? 'archive' : 'actif';
    try {
      await axios.put(`${API}/protocols/${protocol.id}`, {
        ...protocol,
        statut: newStatus
      });
      toast.success(`✅ Protocole ${newStatus === 'actif' ? 'activé' : 'archivé'}`);
      await fetchProtocols();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('❌ Erreur lors du changement de statut');
    }
  };

  // Filtrer les vitamines selon la recherche
  const filteredVitamines = VITAMINES_DISPONIBLES.filter(v => 
    v.toLowerCase().includes(searchVitamine.toLowerCase()) &&
    !formData.vitamines.includes(v)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const activeProtocols = protocols.filter(p => p.statut === 'actif');
  const archivedProtocols = protocols.filter(p => p.statut === 'archive');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Protocoles ({activeProtocols.length} actifs, {archivedProtocols.length} archivés)
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestion des protocoles de perfusion
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="new-protocol-button">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Protocole
        </Button>
      </div>

      {/* Active Protocols */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Protocoles Actifs</h3>
        {activeProtocols.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8 text-gray-500">
                <p>Aucun protocole actif. Créez votre premier protocole !</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeProtocols.map((protocol) => (
              <Card key={protocol.id} className="hover:shadow-md transition-shadow" data-testid={`protocol-${protocol.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-base">{protocol.nom}</span>
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm space-y-1">
                    <p className="text-gray-600">
                      Vitamines: <span className="font-medium">{protocol.vitamines?.length || 0}</span>
                    </p>
                    {protocol.vitamines && protocol.vitamines.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {protocol.vitamines.slice(0, 3).map((v, i) => (
                          <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {v.length > 20 ? v.substring(0, 20) + '...' : v}
                          </span>
                        ))}
                        {protocol.vitamines.length > 3 && (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            +{protocol.vitamines.length - 3} autres
                          </span>
                        )}
                      </div>
                    )}
                    <p className="text-gray-600">Durée: <span className="font-medium">{protocol.duree} min</span></p>
                    <p className="text-gray-600">Débit: <span className="font-medium">{protocol.debit}</span></p>
                  </div>
                  {protocol.description && (
                    <p className="text-sm text-gray-600 italic">{protocol.description}</p>
                  )}
                  <div className="flex space-x-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(protocol)}
                      className="flex-1"
                      data-testid={`edit-protocol-${protocol.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Éditer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(protocol)}
                      title="Dupliquer"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleStatus(protocol)}
                      title="Archiver"
                    >
                      <Archive className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(protocol.id)}
                      className="hover:bg-red-50 hover:text-red-600"
                      title="Supprimer"
                      data-testid={`delete-protocol-${protocol.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Archived Protocols */}
      {archivedProtocols.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Protocoles Archivés</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {archivedProtocols.map((protocol) => (
              <Card key={protocol.id} className="opacity-60 hover:opacity-100 transition-opacity">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-base">{protocol.nom}</span>
                    <Archive className="w-5 h-5 text-gray-400" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm space-y-1">
                    <p className="text-gray-600">Vitamines: <span className="font-medium">{protocol.vitamines?.length || 0}</span></p>
                    <p className="text-gray-600">Durée: <span className="font-medium">{protocol.duree} min</span></p>
                  </div>
                  <div className="flex space-x-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleStatus(protocol)}
                      className="flex-1"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Réactiver
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(protocol.id)}
                      className="hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Protocol Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProtocol ? 'Modifier le Protocole' : 'Nouveau Protocole'}
            </DialogTitle>
            <DialogDescription>
              Configurez les paramètres du protocole de perfusion
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="protocol-nom">Nom du Protocole *</Label>
                <Input
                  id="protocol-nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Cocktail Myers Plus"
                  data-testid="protocol-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="protocol-desc">Description</Label>
                <Input
                  id="protocol-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description optionnelle"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="protocol-dosage">Dosage</Label>
                <select
                  id="protocol-dosage"
                  value={formData.dosage}
                  onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Faible dose">Faible dose</option>
                  <option value="Standard">Standard</option>
                  <option value="Haute dose">Haute dose</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="protocol-debit">Débit (mL/h)</Label>
                <Input
                  id="protocol-debit"
                  type="number"
                  value={formData.debit.toString().replace(' mL/h', '')}
                  onChange={(e) => setFormData({ ...formData, debit: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="protocol-duree">Durée (min)</Label>
                <Input
                  id="protocol-duree"
                  type="number"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Vitamines Selection */}
            <div className="space-y-3">
              <Label>Vitamines et Nutriments *</Label>
              
              {/* Search and Add */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher une vitamine..."
                  value={searchVitamine}
                  onChange={(e) => setSearchVitamine(e.target.value)}
                  className="pl-10"
                  data-testid="vitamin-search-input"
                />
              </div>

              {/* Suggestions dropdown */}
              {searchVitamine && filteredVitamines.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded-md bg-white shadow-sm">
                  {filteredVitamines.slice(0, 10).map((vit, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => addVitamine(vit)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0"
                    >
                      {vit}
                    </button>
                  ))}
                  {filteredVitamines.length > 10 && (
                    <p className="px-3 py-2 text-sm text-gray-500">
                      ... et {filteredVitamines.length - 10} autres résultats
                    </p>
                  )}
                </div>
              )}

              {/* Selected vitamines */}
              <div className="min-h-[60px] p-3 bg-gray-50 rounded-md">
                {formData.vitamines.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune vitamine sélectionnée. Utilisez la recherche ci-dessus.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {formData.vitamines.map((vit, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                      >
                        {vit}
                        <button
                          type="button"
                          onClick={() => removeVitamine(vit)}
                          className="ml-2 hover:text-blue-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500">{formData.vitamines.length} vitamine(s) sélectionnée(s)</p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button onClick={handleSave} data-testid="save-protocol-button">
                {editingProtocol ? 'Enregistrer' : 'Créer le Protocole'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProtocolsManager;
